// frontend/src/utils/dayOfWeekPickerColDef.js
//
// Shared column-def builder for a "pick a day of the week" grid cell —
// a native agRichSelectCellEditor over the fixed 0..6 (Mon..Sun) set with
// typeahead (allowTyping + filterList), matching every other dropdown cell.
//
// Cell value is the integer day index (0 = Monday … 6 = Sunday); the typed
// input is reverse-matched against the localized day name so a merchant can
// type "wed" as well as scroll. Callers supply `formatDay` (an i18n lookup)
// so day names come from one canonical namespace (common.weekdays.*).
//
// `formatDay` receives the ROW as well as the day index, because the same
// stored day can mean different things per row: a MONTHLY supplier's Monday
// is "First Monday" (constants/planning.usesFirstWeekdayOfMonth). Display
// therefore has to be resolved per row, not per column — which is why
// `cellEditorParams` is a function here rather than a static object.
//
// Used by the Supplier setup grid's order-day column
// (pages/supplierPlanningColumns.js).

import { ORDER_DAYS } from '../constants/planning';
import { TYPEAHEAD_LIST_PARAMS } from './referencePickerColDef';

const EM_DASH = '—';

/**
 * @param {object} opts
 * @param {string} opts.field  Row key holding the 0..6 day index.
 * @param {(dayIndex: number, row?: object) => string} opts.formatDay
 *   Localized label for a day index. Called WITH the row for display, and
 *   WITHOUT it to obtain the plain day name — so a caller that varies the
 *   label by row must return the plain name when `row` is undefined. Paste
 *   accepts either form (see valueParser).
 * @param {boolean | ((params: object) => boolean)} [opts.editable=true]
 *   Passed straight to AG Grid — the supplier grid gates this on the row's
 *   OWN anchored cadence (Weekly, Monthly), the cadences that carry a day.
 * @returns {object} Partial column def; caller adds headerName/width/etc.
 */
export function dayOfWeekPickerColDef({ field, formatDay, editable = true }) {
  if (!field || typeof formatDay !== 'function') {
    throw new Error('dayOfWeekPickerColDef: field and formatDay are required');
  }
  const format = (value, row) =>
    value === null || value === undefined ? EM_DASH : formatDay(value, row);

  return {
    field,
    editable,
    cellEditor: 'agRichSelectCellEditor',
    // A function, not an object: the editor's option labels depend on the row
    // being edited. AG Grid documents exactly this shape — "cellEditorParams
    // function allows dynamic props", whose own example is an
    // agRichSelectCellEditor deriving `values`/`formatValue` from
    // `params.data` (AG Grid docs, provided-cell-editors; read for 34.1, the
    // newest the docs MCP serves — this repo runs 36.0.1). The unit test
    // beside this file invokes the function form directly, so the contract is
    // also pinned locally.
    cellEditorParams: (params) => ({
      values: ORDER_DAYS,
      ...TYPEAHEAD_LIST_PARAMS,
      formatValue: (value) => format(value, params.data),
    }),
    valueFormatter: (params) => format(params.value, params.data),
    valueParser: (params) => {
      const raw = params.newValue;
      if (raw === null || raw === undefined || raw === EM_DASH || raw === '') return null;
      if (ORDER_DAYS.includes(raw)) return raw;
      const text = String(raw).toLowerCase();
      // Both spellings are accepted on paste: the row's own label ("First
      // Monday" on a monthly supplier) and the plain day name ("Monday"),
      // which is what the CSV/XLSX column carries and what a merchant
      // copying between rows of different cadences will have on the
      // clipboard. Rejecting the plain name would make the export
      // un-pasteable into the grid it came from.
      const match = ORDER_DAYS.find(
        (d) => format(d, params.data).toLowerCase() === text
          || format(d, undefined).toLowerCase() === text,
      );
      return match !== undefined ? match : params.oldValue;
    },
  };
}
